import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { blogService, BlogPost, BlogCategory } from '../services/blogService';
import { config } from '../config';
import SEO from '../components/common/SEO';
import { useUser } from '../contexts/UserContext';
import { FaPlus, FaEdit } from 'react-icons/fa';
import { Icon } from '../components/common/Icon';

const Container = styled.div`
  margin: 0 auto;
  padding: 2rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin: 0;
  color: white;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  background-color: rgb(31, 31, 31);
  color: white;
  font-size: 1rem;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  background-color: rgb(31, 31, 31);
  color: white;
  font-size: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  option {
    background-color: rgb(31, 31, 31);
    color: white;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.div<{ $isDraft?: boolean }>`
  background-color: rgb(31, 31, 31);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  ${props => props.$isDraft && `
    border: 2px solid #ffc107;
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const DraftBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: #ffc107;
  color: #000;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 1;
`;

const EditButton = styled.button`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  z-index: 2;

  &:hover {
    background-color: #0056b3;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  background-color: #222;
`;

const BlogImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
`;

const BlogContent = styled.div`
  padding: 1.5rem;
`;

const BlogTitle = styled.h2`
  font-size: 1.25rem;
  color: white;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: #999;
`;

const AuthorImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const CategoryBadge = styled.span`
  background-color: #007bff;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const BlogExcerpt = styled.p`
  color: #ccc;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0.75rem 0;
`;

const ReadMore = styled.span`
  color: #007bff;
  font-weight: 500;
  font-size: 0.9rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'oldest'>(
    (searchParams.get('sort') as 'popular' | 'recent' | 'oldest') || 'recent'
  );
  const [showDrafts, setShowDrafts] = useState(false);

  // Note: Database stores as "admin" but TypeScript interface uses is_admin
  const canPostBlog = user?.user_permissions?.post_blog || (user?.user_permissions as any)?.admin || user?.user_permissions?.is_admin;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
    updateURLParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy, keyword, showDrafts]);

  const fetchCategories = async () => {
    try {
      const data = await blogService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await blogService.getPosts({
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        keyword: keyword || undefined,
        sort: sortBy,
        limit: 50,
        drafts: showDrafts,
      });
      setPosts(data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = () => {
    const params = new URLSearchParams();
    if (keyword) params.set('keyword', keyword);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (sortBy !== 'recent') params.set('sort', sortBy);
    setSearchParams(params);
  };

  const handlePostClick = (post: BlogPost, e: React.MouseEvent) => {
    // Don't navigate if clicking on edit button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    
    // If it's a draft, only allow owner to view (will show editor)
    if (!post.is_published && post.user_id === user?.id) {
      navigate(`/blog/edit/${post.id}`);
    } else if (post.is_published) {
      navigate(`/blog/${post.id}/${post.slug}`);
    }
  };

  const handleCreatePost = () => {
    navigate('/blog/create');
  };

  const handleEditPost = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/blog/edit/${postId}`);
  };

  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchPosts();
    }
  };

  return (
    <>
      <SEO 
        title="Blog - YouMeditation"
        description="Read the latest articles about meditation, mindfulness, and wellness from YouMeditation"
        keywords="meditation blog, mindfulness articles, wellness tips, meditation guide"
        url="/blog"
      />
      <Container>
        <HeaderSection>
          <Title>Blog</Title>
          {canPostBlog && (
            <CreateButton onClick={handleCreatePost}>
              <Icon icon={FaPlus} size={16} />
              Create Post
            </CreateButton>
          )}
        </HeaderSection>

        <FilterBar>
          <SearchInput
            type="text"
            placeholder="Search by keyword..."
            value={keyword}
            onChange={handleKeywordChange}
            onKeyPress={handleKeywordKeyPress}
          />
          
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.slug}>
                {category.name}
              </option>
            ))}
          </Select>

          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'popular' | 'recent' | 'oldest')}
          >
            <option value="recent">Recent</option>
            <option value="popular">Popular</option>
            <option value="oldest">Oldest</option>
          </Select>

          {canPostBlog && (
            <Select
              value={showDrafts ? 'drafts' : 'published'}
              onChange={(e) => setShowDrafts(e.target.value === 'drafts')}
            >
              <option value="published">Published</option>
              <option value="drafts">My Drafts</option>
            </Select>
          )}
        </FilterBar>

        {loading ? (
          <LoadingMessage>Loading blog posts...</LoadingMessage>
        ) : posts.length === 0 ? (
          <NoResultsMessage>No blog posts found.</NoResultsMessage>
        ) : (
          <BlogGrid>
            {posts.map((post) => {
              const isOwnDraft = !post.is_published && post.user_id === user?.id;
              return (
                <BlogCard 
                  key={post.id} 
                  onClick={(e) => handlePostClick(post, e)}
                  $isDraft={isOwnDraft}
                >
                  {isOwnDraft && <DraftBadge>DRAFT</DraftBadge>}
                  {post.featured_image ? (
                    <BlogImage src={post.featured_image} alt={post.title} />
                  ) : (
                    <BlogImagePlaceholder>
                      {post.title.charAt(0)}
                    </BlogImagePlaceholder>
                  )}
                  <BlogContent>
                    {post.category_name && (
                      <CategoryBadge>{post.category_name}</CategoryBadge>
                    )}
                    <BlogTitle>{post.title}</BlogTitle>
                    <BlogMeta>
                      {!post.is_anonymous && post.author_username && (
                        <>
                          {post.author_profile_image && (
                            <AuthorImage
                              src={`https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${post.author_profile_image}`}
                              alt={post.author_username}
                            />
                          )}
                          <span>by {post.author_username}</span>
                        </>
                      )}
                    </BlogMeta>
                    <BlogExcerpt>
                      {post.excerpt?.replace(/\s+/g, ' ')}
                    </BlogExcerpt>
                    <ReadMore>Read More →</ReadMore>
                    {isOwnDraft && (
                      <EditButton onClick={(e) => handleEditPost(post.id, e)} title="Edit Draft">
                        <Icon icon={FaEdit} size={16} />
                      </EditButton>
                    )}
                  </BlogContent>
                </BlogCard>
              );
            })}
          </BlogGrid>
        )}
      </Container>
    </>
  );
};

export default Blog;
